/**
 * A skill-edit conversation rebuilt from its local Work Journal — the AutoCAD skill's
 * `threads\<threadId>\journal.jsonl`, served by the agent's
 * `GET /v2/skills/:id/regen-threads/:threadId/journal`.
 *
 * The whole conversation is kept in the thread's workspace, the way a workflow keeps its
 * own: the user's messages with the files they were sent with, the agent's replies, every
 * tool call with its result and images, and the decisions at approval cards. A reopened
 * conversation loads from there and reads the way it streamed: one reply per message, its
 * tool steps and decisions in the order they happened, and the reply's last text as the
 * answer. (The cloud history is still written every turn; it is the fallback for a
 * conversation from before the journal.)
 *
 * Text the agent wrote before a tool call is that call's narration (`note`), as the live
 * stream places it. A call that never got a result was cut short (a stop, a closed window)
 * and shows as stopped, not as running forever.
 */

import type { ChatMessage } from "../hooks/useChatTurn";
import type { MessageAttachment } from "./attachments";
import type { AgentStep, ResumeDecision } from "./chat";
import { decisionStep } from "./hitlCard";
import { parseToolOutput } from "./toolResult";

/** One entry of a skill thread's journal, as the agent writes it (`workspace/journal/types.ts`). */
export type SkillJournalEntry =
  | { type: "user"; text: string; attachments?: MessageAttachment[] }
  | { type: "assistant"; text: string }
  | { type: "tool_call"; id: string; tool: string; displayName?: string; input: unknown }
  | { type: "tool_result"; id: string; ok: boolean; output: unknown; images?: string[] }
  | { type: "decision"; action: { name: string; args: Record<string, unknown> }; decision: ResumeDecision }
  | { type: "continue" };

export type SkillJournalRecord = SkillJournalEntry & { n: number; at: string; truncated?: boolean; bytes?: number };

export function journalToMessages(entries: readonly SkillJournalRecord[]): ChatMessage[] {
  const out: ChatMessage[] = [];
  let reply: (ChatMessage & { steps: AgentStep[] }) | null = null;
  // What the agent last said that no tool call has claimed yet: the answer, unless a call follows.
  let narration: string | null = null;
  const pending = new Map<string, AgentStep>();

  const closeReply = () => {
    if (!reply) return;
    reply.text = narration ?? "";
    for (const step of reply.steps) if (step.status === "running") step.status = "stopped";
    if (reply.text || reply.steps.length > 0) out.push(reply);
    reply = null;
    narration = null;
    pending.clear();
  };
  const openReply = (n: number) => {
    reply ??= { id: `j-${n}`, role: "assistant", text: "", steps: [] };
    return reply;
  };

  for (const entry of entries) {
    switch (entry.type) {
      case "user": {
        closeReply();
        const attachments = Array.isArray(entry.attachments) ? entry.attachments : [];
        // "[Attachments]" is what a files-only message is filed as; the files say it better.
        const text = attachments.length > 0 && entry.text === "[Attachments]" ? "" : entry.text;
        if (text.trim() || attachments.length > 0) {
          out.push({ id: `j-${entry.n}`, role: "user", text, ...(attachments.length > 0 ? { attachments } : {}) });
        }
        break;
      }
      case "assistant":
        openReply(entry.n);
        narration = narration ? `${narration}\n\n${entry.text}` : entry.text;
        break;
      case "tool_call": {
        const current = openReply(entry.n);
        const step: AgentStep = {
          tool: entry.tool,
          ...(entry.id ? { id: entry.id } : {}),
          status: "running",
          ...(entry.displayName ? { label: entry.displayName } : {}),
          ...(narration ? { note: narration } : {}),
        };
        narration = null;
        current.steps.push(step);
        if (entry.id) pending.set(entry.id, step);
        break;
      }
      case "tool_result": {
        const step = pending.get(entry.id);
        if (!step) break;
        pending.delete(entry.id);
        step.status = entry.ok ? "completed" : "error";
        const parsed = parseToolOutput(entry.output);
        step.result = parsed.execution ?? {
          success: entry.ok,
          output: typeof entry.output === "string" ? entry.output : JSON.stringify(entry.output),
        };
        // Journal paths for now; the editor turns them into URLs it can show (`journalImageUrls`).
        if (Array.isArray(entry.images) && entry.images.length > 0) step.images = [...entry.images];
        break;
      }
      case "decision": {
        // Where the user decided, as the live chat drew it: the approved call, if any, follows.
        const current = openReply(entry.n);
        if (narration) {
          current.steps.push({ ...decisionStep(entry.action, entry.decision), note: narration });
          narration = null;
        } else {
          current.steps.push(decisionStep(entry.action, entry.decision));
        }
        break;
      }
      default:
        // `continue` marks a cut-off run picked up again: nothing to show.
        break;
    }
  }
  closeReply();
  return out;
}

/** Every journal image path the steps name, once. */
export function journalImagePaths(messages: readonly ChatMessage[]): string[] {
  const paths = new Set<string>();
  for (const message of messages) for (const step of message.steps ?? []) for (const image of step.images ?? []) paths.add(image);
  return [...paths];
}

/** The steps with their journal image paths replaced by URLs; a path with none is dropped. */
export function withImageUrls(messages: readonly ChatMessage[], urls: ReadonlyMap<string, string>): ChatMessage[] {
  return messages.map((message) => message.steps?.some((step) => step.images?.length)
    ? {
        ...message,
        steps: message.steps.map((step) => {
          if (!step.images?.length) return step;
          const shown = step.images.map((path) => urls.get(path)).filter((url): url is string => typeof url === "string");
          const { images: _images, ...rest } = step;
          return shown.length > 0 ? { ...rest, images: shown } : rest;
        }),
      }
    : message);
}
